import React, { useState, useMemo, useEffect } from 'react';
import { useUsers, ROLE_LABELS } from '../../context/UsersContext';
import { useClients } from '../../context/ClientsContext';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import InviteUserModal from './InviteUserModal';
import EditUserModal from './EditUserModal';

export default function UserManagement() {
  const { users, currentUser, getUserStats, isSuperUser, loadUsers } = useUsers();
  const { clients, getAllPortalContacts, disablePortalAccess, resetContactPassword } = useClients();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'internal', 'employers'
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // Track which user's menu is open
  const [confirmAction, setConfirmAction] = useState(null); // For delete/archive confirmation

  // Load users on mount and when currentUser changes (to catch profile updates)
  useEffect(() => {
    if (isSuperUser()) {
      loadUsers();
    }
  }, [isSuperUser, loadUsers, currentUser]);

  const stats = getUserStats();
  
  // Get all employer contacts
  const employerContacts = useMemo(() => getAllPortalContacts(), [clients]);
  
  // Combined stats
  const combinedStats = {
    total: stats.total + employerContacts.length,
    internal: stats.total,
    employers: employerContacts.length,
    active: stats.active,
    invited: stats.invited,
    deactivated: stats.deactivated,
  };
  
  // Filtered list based on current filter
  const filteredUsers = useMemo(() => {
    const internalUsers = users.map(u => ({ ...u, type: 'internal' }));
    const contactUsers = employerContacts.map(c => ({ ...c, type: 'employer' }));
    
    if (filter === 'internal') return internalUsers;
    if (filter === 'employers') return contactUsers;
    return [...internalUsers, ...contactUsers];
  }, [users, employerContacts, filter]);

  // Get relative time string
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Online now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
  };

  // Get client count for a user
  const getClientCount = (userId) => {
    if (isSuperUser({ id: userId, role: users.find(u => u.id === userId)?.role })) {
      return `All (${clients.length})`;
    }
    return clients.filter(c => c.crm.assignedTo === userId).length;
  };

  // Archive user
  const handleArchiveUser = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('healthcues_token')}`
        }
      });

      if (response.ok) {
        alert('User archived successfully');
        loadUsers();
        setActionMenuOpen(null);
        setConfirmAction(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to archive user');
      }
    } catch (error) {
      alert('An error occurred');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('healthcues_token')}`
        }
      });

      if (response.ok) {
        alert('User deleted successfully');
        loadUsers();
        setActionMenuOpen(null);
        setConfirmAction(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      alert('An error occurred');
    }
  };

  // Resend invitation
  const handleResendInvitation = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/resend-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('healthcues_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.emailSent) {
          alert(`Invitation email sent successfully!\n\nTemporary password: ${result.tempPassword}`);
        } else {
          alert(`Invitation created but email failed to send.\n\nTemporary password: ${result.tempPassword}\n\nPlease manually share this with the user.`);
        }
        loadUsers();
        setActionMenuOpen(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to resend invitation');
      }
    } catch (error) {
      alert('An error occurred');
    }
  };

  // Status indicator
  const StatusBadge = ({ status }) => {
    const colors = {
      active: { bg: '#e7f7ed', color: '#0d7c3a', icon: '🟢' },
      invited: { bg: '#fff4e6', color: '#b86e00', icon: '🟡' },
      deactivated: { bg: '#f5f5f5', color: '#666', icon: '⚫' },
    };
    const style = colors[status] || colors.active;
    
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 18,
      }}>
        {style.icon}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        ...S.card,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 4 }}>
            User Management
          </h2>
          <p style={{ fontSize: 12, color: C.textMid, margin: 0 }}>
            {combinedStats.total} users total · {combinedStats.internal} internal · {combinedStats.employers} employer contacts
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          style={{
            padding: '10px 18px',
            background: C.accent,
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon name="person-add-outline" style={{ fontSize: 16 }} />
          Invite User
        </button>
      </div>
      
      {/* Filters */}
      <div style={{
        ...S.card,
        padding: '12px 16px',
        marginBottom: 16,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textMid, marginRight: 8 }}>
          Filter:
        </span>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 600,
            color: filter === 'all' ? 'white' : C.text,
            background: filter === 'all' ? C.accent : 'transparent',
            border: `1px solid ${filter === 'all' ? C.accent : C.border}`,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          All Users ({combinedStats.total})
        </button>
        <button
          onClick={() => setFilter('internal')}
          style={{
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 600,
            color: filter === 'internal' ? 'white' : C.text,
            background: filter === 'internal' ? C.accent : 'transparent',
            border: `1px solid ${filter === 'internal' ? C.accent : C.border}`,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Internal Only ({combinedStats.internal})
        </button>
        <button
          onClick={() => setFilter('employers')}
          style={{
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 600,
            color: filter === 'employers' ? 'white' : C.text,
            background: filter === 'employers' ? C.accent : 'transparent',
            border: `1px solid ${filter === 'employers' ? C.accent : C.border}`,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Employer Contacts ({combinedStats.employers})
        </button>
      </div>

      {/* Users Table */}
      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{
              borderBottom: `2px solid ${C.border}`,
              background: C.bg,
            }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', width: '60px' }}>
                Status
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase' }}>
                User
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase' }}>
                Role
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase' }}>
                Company
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', width: '120px' }}>
                Last Active
              </th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', width: '100px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const isEmployer = user.type === 'employer';
              const status = isEmployer ? user.portalStatus : user.status;
              
              return (
                <tr
                  key={`${user.type}-${user.id}`}
                  style={{
                    borderBottom: `1px solid ${C.border}`,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '16px' }}>
                    <StatusBadge status={status} />
                  </td>
                  
                  <td style={{ padding: '16px' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                        {user.firstName} {user.lastName}
                        {!isEmployer && user.id === currentUser.id && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: C.textMid, fontWeight: 500 }}>(You)</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMid }}>
                        {user.email}
                      </div>
                    </div>
                  </td>
                  
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {isEmployer ? (
                        <>
                          <Icon name="business-outline" style={{ fontSize: 16, color: C.accent }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                            {user.role || 'Contact'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Icon name={user.role === 'super_user' ? 'shield-checkmark-outline' : 'person-outline'} style={{ fontSize: 16, color: C.accent }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                            {ROLE_LABELS[user.role] || user.role}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  
                  <td style={{ padding: '16px' }}>
                    {isEmployer ? (
                      <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>
                        {user.clientName}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: C.textMid }}>
                        —
                      </div>
                    )}
                  </td>
                  
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: 12, color: C.textMid }}>
                      {user.lastLoginAt ? getRelativeTime(user.lastLoginAt) : (status === 'invited' ? 'Invited' : 'Never')}
                    </div>
                  </td>
                  
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {isEmployer ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const result = resetContactPassword(user.clientId, user.id, currentUser.id);
                              if (result.success) {
                                alert(`New password for ${user.firstName} ${user.lastName}:\n\n${result.tempPassword}\n\nShare this securely.`);
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.accent,
                              background: 'transparent',
                              border: `1px solid ${C.accent}`,
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                            title="Reset Password"
                          >
                            Reset
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Disable portal access for ${user.firstName} ${user.lastName}?`)) {
                                disablePortalAccess(user.clientId, user.id, currentUser.id);
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.orange,
                              background: 'transparent',
                              border: `1px solid ${C.orange}`,
                              borderRadius: 4,
                              cursor: 'pointer',
                            }}
                            title="Disable Access"
                          >
                            Disable
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to client drawer
                              window.location.hash = `#client-${user.clientId}`;
                            }}
                            style={{
                              padding: '4px 8px',
                              fontSize: 10,
                              fontWeight: 600,
                              color: C.text,
                              background: C.bg,
                              border: `1px solid ${C.border}`,
                              borderRadius: 4,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                            title="Go to Client"
                          >
                            <Icon name="arrow-forward-outline" style={{ fontSize: 12 }} />
                          </button>
                        </>
                      ) : (
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionMenuOpen(actionMenuOpen === user.id ? null : user.id);
                            }}
                            data-testid={`user-actions-menu-${user.id}`}
                            style={{
                              padding: '6px 12px',
                              fontSize: 11,
                              fontWeight: 600,
                              color: C.text,
                              background: '#fff',
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            Actions
                            <Icon name="chevron-down-outline" style={{ fontSize: 14 }} />
                          </button>

                          {/* Dropdown Menu */}
                          {actionMenuOpen === user.id && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: 4,
                                background: '#fff',
                                border: `1px solid ${C.border}`,
                                borderRadius: 8,
                                boxShadow: '0 10px 30px rgba(2, 6, 23, 0.08)',
                                zIndex: 100,
                                minWidth: 180
                              }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUser(user);
                                  setActionMenuOpen(null);
                                }}
                                data-testid="action-edit-user"
                                style={{
                                  width: '100%',
                                  padding: '10px 14px',
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: C.text,
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  borderBottom: `1px solid ${C.border}`
                                }}
                              >
                                <Icon name="create-outline" style={{ fontSize: 16, color: C.accent }} />
                                Edit User
                              </button>

                              {user.status === 'invited' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResendInvitation(user.id);
                                  }}
                                  data-testid="action-resend-invitation"
                                  style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: C.text,
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    borderBottom: `1px solid ${C.border}`
                                  }}
                                >
                                  <Icon name="mail-outline" style={{ fontSize: 16, color: C.teal }} />
                                  Resend Invitation
                                </button>
                              )}

                              {user.status !== 'deactivated' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmAction({ type: 'archive', userId: user.id, userName: `${user.firstName} ${user.lastName}` });
                                    setActionMenuOpen(null);
                                  }}
                                  data-testid="action-archive-user"
                                  style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: C.text,
                                    background: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    borderBottom: `1px solid ${C.border}`
                                  }}
                                >
                                  <Icon name="archive-outline" style={{ fontSize: 16, color: C.orange }} />
                                  Archive User
                                </button>
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmAction({ type: 'delete', userId: user.id, userName: `${user.firstName} ${user.lastName}` });
                                  setActionMenuOpen(null);
                                }}
                                data-testid="action-delete-user"
                                style={{
                                  width: '100%',
                                  padding: '10px 14px',
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: C.red,
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10
                                }}
                              >
                                <Icon name="trash-outline" style={{ fontSize: 16, color: C.red }} />
                                Delete User
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMid }}>
            No users found
          </div>
        )}
      </div>

      {/* Modals */}
      {showInviteModal && (
        <InviteUserModal onClose={() => setShowInviteModal(false)} />
      )}

      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />
      )}

      {/* Confirmation Dialog for Archive/Delete */}
      {confirmAction && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setConfirmAction(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              maxWidth: 420,
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Icon
                name={confirmAction.type === 'delete' ? 'trash-outline' : 'archive-outline'}
                style={{ fontSize: 28, color: confirmAction.type === 'delete' ? C.red : C.orange }}
              />
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {confirmAction.type === 'delete' ? 'Delete' : 'Archive'} User?
              </h3>
            </div>

            <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24, lineHeight: 1.6 }}>
              {confirmAction.type === 'delete' ? (
                <>
                  Are you sure you want to <strong style={{ color: C.red }}>permanently delete</strong>{' '}
                  <strong>{confirmAction.userName}</strong>? This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to <strong style={{ color: C.orange }}>archive</strong>{' '}
                  <strong>{confirmAction.userName}</strong>? They will be deactivated and unable to log in.
                </>
              )}
            </p>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  padding: '10px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: C.text,
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmAction.type === 'delete') {
                    handleDeleteUser(confirmAction.userId);
                  } else {
                    handleArchiveUser(confirmAction.userId);
                  }
                }}
                data-testid={`confirm-${confirmAction.type}-user`}
                style={{
                  padding: '10px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  background: confirmAction.type === 'delete' ? C.red : C.orange,
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {confirmAction.type === 'delete' ? 'Delete User' : 'Archive User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
